import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";

import { FilterChips, FilterPanel } from "components/shared/FilterPanel/FilterPanel";
import { useFilterPanel } from "components/shared/FilterPanel/useFilterPanel";
import RefreshButton from "components/shared/RefreshButton/RefreshButton";
import UserProfileButton from "components/shared/UserProfileButton/UserProfileButton";
import BudgetUsageCell, { SCOPE_LABELS } from "pages/admin/components/CopilotDashboard/BudgetUsageCell";
import UserDetailModal from "pages/admin/components/CopilotDashboard/UserDetailModal";
import { getPresetRange } from "pages/admin/components/CopilotDashboard/datePresets";
import AvatarRandomColor from "pages/OrgHistory/components/ReusableDataGrid/AvatarRandomColor";
import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetCostCenterListQuery, useGetCostCenterMembersQuery, useLazyGetUserQuery } from "state/rtkQuery/user/userQueries";
import { useAccessPlatformId } from "utils/accessManagement/useAccessPlatformId";
import { generateRandomColor } from "utils/generateRandomColor";

import { PoolBar, StateChip } from "./EcrOpsCostCenters";
import { SCOPE_FIELDS, asList, formatCostCenterDate } from "./costCenters/costCenterDisplay";

const ACCENT = "#26890d";
const PER_PAGE = 10;
const DEFAULT_SORT = { by: "first_name", order: "asc" };

const FILTER_FIELDS = [
  { key: "search", label: "Email", type: "text", placeholder: "Search by email" },
  {
    key: "has_copilot_license",
    label: "Copilot License",
    type: "select",
    options: [
      { value: "true", label: "Has a license" },
      { value: "false", label: "No license" },
    ],
  },
  {
    key: "budget_scope",
    label: "Budget Type",
    type: "select",
    options: Object.entries(SCOPE_LABELS).map(([value, label]) => ({ value, label })),
  },
];

const headCellSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "#999",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  backgroundColor: "#fafafa",
  borderBottom: "2px solid #e8e8e8",
  py: 1.5,
};
const bodyCellSx = { fontSize: "0.85rem", color: "#1a1a1a", py: 1.2, borderBottom: "1px solid #f0f0f0" };

const getAvatarName = (user) => {
  const first = user.first_name?.trim();
  const last = user.last_name?.trim();
  if (first || last) return { firstName: first ?? "", lastName: last ?? "" };
  const fallback = user.user_name ?? user.email ?? "?";
  return { firstName: fallback, lastName: "" };
};

const getCurrentMonthRange = () => {
  const { from, to } = getPresetRange("this_month");
  return { from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") };
};

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#94a3b8", letterSpacing: 0.6, textTransform: "uppercase" }}>{label}</Typography>
    <Box sx={{ fontSize: "0.88rem", color: "#1e293b", fontWeight: 500, wordBreak: "break-word", mt: 0.2 }}>{children}</Box>
  </Box>
);

const IdField = ({ label, value }) => (
  <Field label={label}>
    {value ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography component="code" sx={{ fontSize: "0.78rem", fontFamily: "monospace", wordBreak: "break-all" }}>
          {value}
        </Typography>
        <Tooltip title={`Copy ${label}`} arrow>
          <IconButton size="small" aria-label={`Copy ${label}`} onClick={() => navigator.clipboard?.writeText(String(value))} sx={{ p: "2px" }}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 13, color: "#9ca3af" }} />
          </IconButton>
        </Tooltip>
      </Box>
    ) : (
      "—"
    )}
  </Field>
);

const ChipList = ({ values }) =>
  values.length === 0 ? (
    <Typography component="span" sx={{ color: "#bbb" }}>
      —
    </Typography>
  ) : (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {values.map((value) => (
        <Chip key={value} size="small" label={value} sx={{ fontSize: "0.72rem", height: 22 }} />
      ))}
    </Box>
  );

// ECR Ops → Cost Centers → one cost center: its details, and the users in it with their Copilot budget.
const EcrOpsCostCenterDetail = () => {
  const { costCenterId } = useParams();
  const navigate = useNavigate();
  const { platformId } = useAccessPlatformId();

  // ── Details ──
  // state is left off so the cost center loads whether it is active or deleted.
  const { data: found, isFetching: isLoadingDetails, isError: detailsError, refetch: refetchDetails } = useGetCostCenterListQuery(
    { platformId, costCenterId },
    { skip: !platformId || !costCenterId }
  );
  const cost = found?.[0] ?? null;

  // ── Users ──
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const { filters, setFilters, query: filterQuery } = useFilterPanel(FILTER_FIELDS, () => setPage(1));
  const {
    data: members,
    isFetching: isLoadingMembers,
    isError: membersError,
    refetch: refetchMembers,
  } = useGetCostCenterMembersQuery(
    {
      platformId,
      costCenterId,
      page,
      perPage: PER_PAGE,
      search: filterQuery.search,
      hasCopilotLicense: filterQuery.has_copilot_license,
      budgetScope: filterQuery.budget_scope,
      sortBy: sort.by,
      sortOrder: sort.order,
    },
    { skip: !platformId || !costCenterId }
  );
  const users = members?.users ?? [];
  const total = members?.total ?? 0;
  const nPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const handleSort = (by) => {
    setSort((current) => (current.by === by ? { by, order: current.order === "asc" ? "desc" : "asc" } : { by, order: "asc" }));
    setPage(1);
  };
  const SortHeader = ({ label, field }) => (
    <TableSortLabel active={sort.by === field} direction={sort.by === field ? sort.order : "asc"} onClick={() => handleSort(field)}>
      {label}
    </TableSortLabel>
  );

  // ── The merged user modal (profile + this month's Copilot usage) ──
  const { data: allOrgs } = useGetActiveOrganizationsQuery();
  const orgIdToName = useMemo(() => Object.fromEntries((allOrgs ?? []).map((org) => [org._id, org.name])), [allOrgs]);
  const [triggerGetUser] = useLazyGetUserQuery();
  const [selectedUser, setSelectedUser] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const monthRange = useMemo(getCurrentMonthRange, []);

  const handleOpenUser = async (row) => {
    setSelectedUser(row);
    setProfileUser(null);
    setProfileError("");
    setIsProfileLoading(true);
    try {
      const result = await triggerGetUser({ email: row.email, platformId });
      const list = Array.isArray(result?.data) ? result.data : [];
      setProfileUser(list.find((u) => u._id === row.user_id) || list[0] || null);
      if (!list.length) setProfileError("Could not find user details for this user.");
    } catch (err) {
      setProfileError("Failed to load user details. Please try again.");
    } finally {
      setIsProfileLoading(false);
    }
  };

  const refreshAll = () => {
    refetchDetails();
    refetchMembers();
  };

  return (
    <div>
      {/* Shared ECR Ops page header, like every other ops page */}
      <div className="ecr-ops-page-header">
        <p className="ecr-ops-page-header__eyebrow">ECR Ops Portal · Cost Centers</p>
        <h1 className="ecr-ops-page-header__title">
          {cost?.name ?? "Cost center"}
          {cost && (
            <Box component="span" sx={{ ml: 1.5, verticalAlign: "middle" }}>
              <StateChip state={cost.state} />
            </Box>
          )}
        </h1>
        <p className="ecr-ops-page-header__sub">Details and the users in this cost center.</p>
      </div>

      <div className="ecr-ops-content" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Back to Cost Centers" arrow>
          <IconButton size="small" aria-label="Back to Cost Centers" onClick={() => navigate("/ecr-ops/cost-centers")} sx={{ color: "#555", ml: -0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>All cost centers</Typography>
        <Box sx={{ ml: "auto" }}>
          <RefreshButton label="Refresh cost center" isRefreshing={isLoadingDetails || isLoadingMembers} onClick={refreshAll} />
        </Box>
      </Box>

      {/* Details */}
      <Paper elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: "12px", p: 2.5 }}>
        {isLoadingDetails && !cost ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} sx={{ color: ACCENT }} />
          </Box>
        ) : detailsError ? (
          <Typography sx={{ color: "#C53211" }}>Couldn't load this cost center. Check the link and try again.</Typography>
        ) : !cost ? (
          <Typography sx={{ color: "#999", fontStyle: "italic" }}>This cost center wasn't found on the selected platform.</Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#0b756c", letterSpacing: "0.08em", textTransform: "uppercase", mb: 1.5 }}>Details</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "14px 28px" }}>
              <IdField label="Cost center ID" value={cost._id} />
              <IdField label="Reference ID" value={cost.ref_id} />
              <IdField label="Platform ID" value={cost.platform_id} />
              <IdField label="Subscription ID" value={cost.subscription_id} />
              <Field label="Created">{formatCostCenterDate(cost.created_date)}</Field>
              <Field label="Last updated">{formatCostCenterDate(cost.updated_date)}</Field>
            </Box>

            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#0b756c", letterSpacing: "0.08em", textTransform: "uppercase", mt: 2.5, mb: 1.5 }}>AI credits</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "14px 28px", alignItems: "start" }}>
              <Field label="Credit pool">
                <PoolBar cost={cost} />
              </Field>
              <Field label="Pool">
                <Chip size="small" label={cost.is_pool_enabled ? "Enabled" : "Not enabled"} sx={{ fontSize: "0.72rem" }} />
              </Field>
              <Field label="Allocation">
                <Chip
                  size="small"
                  label={cost.is_allocation_disabled ? "Disabled" : "Enabled"}
                  sx={{ fontSize: "0.72rem", color: cost.is_allocation_disabled ? "#C53211" : "#555", backgroundColor: cost.is_allocation_disabled ? "rgba(197,50,17,0.1)" : "#f1f1f1" }}
                />
              </Field>
            </Box>

            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#0b756c", letterSpacing: "0.08em", textTransform: "uppercase", mt: 2.5, mb: 1.5 }}>Who it covers</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "14px 28px" }}>
              {SCOPE_FIELDS.map(({ key, label }) => (
                <Field key={key} label={label}>
                  <ChipList values={asList(cost[key])} />
                </Field>
              ))}
            </Box>
          </>
        )}
      </Paper>

      {/* Users */}
      <Paper elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", p: 2, borderBottom: "1px solid #f0f0f0" }}>
          <Box>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>Users</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "#999" }}>
              {members ? `${total.toLocaleString("en-AU")} user${total === 1 ? "" : "s"} in this cost center` : "Loading…"}
            </Typography>
          </Box>
          <FilterPanel fields={FILTER_FIELDS} filters={filters} onFiltersChange={setFilters} height={34} />
        </Box>
        <Box sx={{ px: 2, pt: 1 }}>
          <FilterChips fields={FILTER_FIELDS} filters={filters} onFiltersChange={setFilters} />
        </Box>

        {isLoadingMembers && !members ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={24} sx={{ color: ACCENT }} />
          </Box>
        ) : membersError ? (
          <Typography sx={{ p: 4, color: "#C53211", textAlign: "center" }}>Couldn't load the users. Please refresh and try again.</Typography>
        ) : users.length === 0 ? (
          <Typography sx={{ p: 5, color: "#999", textAlign: "center", fontStyle: "italic" }}>No users match.</Typography>
        ) : (
          <TableContainer sx={{ opacity: isLoadingMembers ? 0.55 : 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 56 }}>#</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 90 }} align="center">
                    Profile
                  </TableCell>
                  <TableCell sx={headCellSx} sortDirection={sort.by === "first_name" ? sort.order : false}>
                    <SortHeader label="User" field="first_name" />
                  </TableCell>
                  <TableCell sx={headCellSx} sortDirection={sort.by === "email" ? sort.order : false}>
                    <SortHeader label="Email" field="email" />
                  </TableCell>
                  <TableCell sx={headCellSx} sortDirection={sort.by === "current_budget" ? sort.order : false}>
                    <SortHeader label="Copilot budget" field="current_budget" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, idx) => (
                  <TableRow key={user.user_id} sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell sx={{ ...bodyCellSx, color: "#999" }}>{(page - 1) * PER_PAGE + idx + 1}</TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <UserProfileButton
                        userLabel={user.email ?? "this user"}
                        onClick={() => handleOpenUser(user)}
                        // A user with no Copilot budget record at all has no license.
                        licensedOrgNames={user.budget ? ["this platform"] : []}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <AvatarRandomColor {...getAvatarName(user)} color={generateRandomColor(user.user_id)} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, lineHeight: 1.3 }}>{user.full_name ?? user.user_name ?? "—"}</Typography>
                          {user.user_name && <Typography sx={{ fontSize: "0.72rem", color: "#999" }}>@{user.user_name}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{user.email ?? "—"}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <BudgetUsageCell budget={user.budget} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {total > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, px: 3, py: 1.5, borderTop: "1px solid #f0f0f0", backgroundColor: "#fafafa" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
              Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} of {total.toLocaleString("en-AU")} users
            </Typography>
            {nPages > 1 && <Pagination nPages={nPages} currentPage={page} setCurrentPage={setPage} />}
          </Box>
        )}
      </Paper>

      <UserDetailModal
        open={Boolean(selectedUser)}
        onClose={() => {
          setSelectedUser(null);
          setProfileUser(null);
          setProfileError("");
        }}
        user={selectedUser}
        costCenterId={costCenterId}
        platformId={platformId}
        fromDate={monthRange.from}
        toDate={monthRange.to}
        footerNote="Showing current month usage only."
        profile={{
          user: profileUser,
          orgIdToName,
          costCenterById: cost ? { [cost._id]: cost.name } : {},
          isLoading: isProfileLoading,
          error: profileError,
        }}
      />
      </div>
    </div>
  );
};

export default EcrOpsCostCenterDetail;
